import React from "react";

export const getActiveEnv = (getActualValue = false, maskDevAsStage = true, maskReleaseAsProd = true) => {
	let activeEnv = process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV || "development";
	// Checking for value masking.
	if (!getActualValue) {
		if ((activeEnv === "dev" || activeEnv === "development") && maskDevAsStage) {
			activeEnv = "stage";
		}
		else if ((activeEnv === "release" || activeEnv === "production") && maskReleaseAsProd) {
			activeEnv = "prod";
		}
		else{
			// no action needed
		}
	}
 	return activeEnv;
}

export const isEDMode = typeof window !== 'undefined' ? window.location.href.indexOf('ed=1') > 0 : false;
export const tabletMedia = "(min-width: 0px) and (max-width: 1024px)";

const matchMediaQuery = media => {
	if (typeof window !== 'undefined') {
		return window.matchMedia(media).matches;
	}
	return true;
}

export const matchesBreakpoint1280 = (media = "(min-width: 0px) and (max-width: 1279px)") => matchMediaQuery(media);

export const isMobile = (media = "(min-width: 0px) and (max-width: 1200px)") => matchMediaQuery(media);

export const isTablet = (media = tabletMedia) => matchMediaQuery(media);

export const bounds = elem => {
	const rect = elem.getBoundingClientRect();

	return {
		top: rect.top,
		left: rect.left,
		right: rect.right,
		bottom: rect.bottom,
		width: rect.width,
		height: rect.height,
	};
};

export const getWinSize = () => {
	if (typeof window !== 'undefined') {
		const w = window;
		const d = document;
		const e = d.documentElement;
		const g = d.getElementsByTagName('body')[0];
		const x = w.innerWidth || e.clientWidth || g.clientWidth;
		const y = w.innerHeight || e.clientHeight || g.clientHeight;

		return { width: x, height: y };
	}

	return { width: 0, height: 0 };
};

export const inViewport = (elem, entireBox = false, _xOffset = 0, _yOffset = 0) => {
	if (typeof window !== 'undefined') {
		const bds = bounds(elem);
		const doc = getWinSize();

		if (!entireBox) {
			return bds.top >= 0 && bds.left >= 0 && bds.top <= doc.height && bds.left <= doc.width;
		} else {
			const topLimit = bds.top - doc.height
			const bottomLimit = bds.top + bds.height;
			const leftLimit = bds.left - doc.width;
			const rightLimit = bds.left + bds.width;

			return topLimit <= 0 && bottomLimit >= 0 && leftLimit <= 0 && rightLimit >= 0;
		}
	}

	return false;
};

export const debounce = (func, wait, immediate) => {
	let timeout = null;

	return function() {
		const context = this;
		const args = arguments;

		const later = () => {
			timeout = null;

			if (!immediate) {
				func.apply(context, args);
			}
		};

		const callNow = immediate && !timeout;

		clearTimeout(timeout);

		timeout = setTimeout(later, wait);

		if (callNow) {
			func.apply(context, args);
		}
	};
};

export const getOS = () => {
	let OSName = 'unknown-os';

	if (navigator.userAgent.includes('Win')) OSName = 'win';
	if (navigator.userAgent.includes('Mac')) OSName = 'mac';
	if (navigator.userAgent.includes('X11')) OSName = 'unix';
	if (navigator.userAgent.includes('iPhone')) OSName = 'ios';
	if (navigator.userAgent.includes('iPad')) OSName = 'ios';
	if (navigator.userAgent.includes('Linux')) OSName = 'linux';

	return OSName;
};

export const getCookieByName = cookieName => {
	const cookieValue = document.cookie.split('; ').find(row => row.startsWith(cookieName + '='));
	if (cookieValue === undefined) {
		return null;
	}
	return cookieValue.split('=')[1];
};

export const focusLoop = (e, selector) => {
	const TAB_KEY_CODE = 9;
	const tabPressed = e.key === 'Tab' || e.keyCode === TAB_KEY_CODE;

	if (!tabPressed) return;

	const elems = document.querySelectorAll(selector)

	if (elems?.length) {
		const firstElem = elems[0];
		const lastElem = elems[elems.length - 1];

		if (!Array.from(elems).includes(document.activeElement)) {
			firstElem.focus();
		}

		if (e.shiftKey) {
			if (document.activeElement === firstElem) {
				lastElem.focus();
				e.preventDefault();
			}
		} else {
			if (document.activeElement === lastElem) {
				firstElem.focus();
				e.preventDefault();
			}
		}
	}
}

export const getUrlParameter = param => {
    param = param.replace(/[\\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + param + '=([^&#]*)');
    const results = regex.exec(window.location.search);

    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

export const extractTextFromElement = element => {
	// Convert element to string first
	let text = '';

	if (typeof element === "string") {
		text = element;
	} else if (React.isValidElement(element)) {
		const children = element.props?.children;
		if (Array.isArray(children)) {
			text = children.map(extractTextFromElement).join(" ");
		} else if (children) {
			text = extractTextFromElement(children);
		} else {
			// no action needed
		}
	} else if (Array.isArray(element)) {
		text = element.map(extractTextFromElement).join(" ");
	} else if (element) {
		text = String(element);
	} else {
		// no action needed
	}

	// Clean up the text by removing HTML tags and common entities
	return text
		.replace(/<[^>]*>/g, ' ')
		.replace(/&nbsp;/g, ' ')
		.replace(/&/g, ' & ')
		.replace(/\s+/g, ' ')
		.trim();
};


// Function to flatten the navigation data
export const flattenNavigation = navData => {
	let links = [];
	navData.forEach(item => {
		if (item.link) {
			links.push({
				link: item.link,
				label: item.arialabel || item.name
			});
		}
		if (item.subMenuItems) {
			item.subMenuItems.forEach(subItem => {
				links.push({
					link: subItem.link,
					label: subItem.arialabel || subItem.name
				});
			});
		}
	});
	return links;
};

export const scrollToSection = (selector, offset = 0) => {
	if (typeof window !== "undefined") {
		const el = document.querySelector(selector);
		if (el) {
			const elementPosition =
				el.getBoundingClientRect().top + window.scrollY - offset;

			window.scrollTo({
				top: elementPosition,
				behavior: "smooth",
			});
		}
	}
};
